import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    clearTables,
    closeTable,
    loadParameters,
    PosActions, setAreas, setCategories,
    setChildArticles, setDevices,
    setDropdownOptions,
    setDropdownParameters,
    setFeatured,
    setMaterials,
    setMenu, setPosOptionsOverrides,
    setTableMap,
    setTables, updateTable
} from '@pos-shop-shared/store/pos.actions';
import { filter, switchMap, take, tap, withLatestFrom, map } from '@proman/rxjs-common';
import { Entity } from '@proman/services/entity.service';
import { Store } from '@ngrx/store';
import { getPosTables, selectPosState } from '@pos-shop-shared/store/pos.selectors';
import { getCurrUser, setCurrUser, userLogout } from '@proman/store/curr-user';
import {
    Area,
    Article, Device,
    OrderConsumer,
    ParameterDropdownOption,
    SystemOptions
} from '@proman/interfaces/entity-interfaces';
import { isDefined, numberDecimalComma, roundNumber, safeMapId } from '@proman/utils';
import {
    OPTIONS_ITEMS_IN_WAREHOUSE,
    OPTIONS_PRINTER_ID,
    OPTIONS_TABLE_LAYOUT,
    OPTIONS_TIME_RESTRICTION
} from "@pos-shared/options";
import { LocalStorageService } from '@proman/services/local-storage.service';
import { CurrenciesService } from '@proman/services/currencies.service';
import { ToastService } from '@proman/services/toast.service';
import { FilterService } from '@proman/services/filter.service';
import { SystemOptionsActions } from '@proman/store/system-options';
import { LoggerService } from '@proman/services/logger.service';
import { RequestService } from '@proman/services/request.service';
import { CONFIG } from '@proman/config';

@Injectable()
export class PosEffects {
    lastTableLoadTimeStamp: number;
    LOGGER = inject(LoggerService);

    loadTables$ = createEffect(() => this.actions$
            .pipe(
                ofType(PosActions.LoadTables),
                withLatestFrom(this.store.select(selectPosState), this.store.select(getCurrUser)),
                filter(([action, state, currUser]) => {
                    return !isDefined(state.tables) || (this.lastTableLoadTimeStamp + 2 * 1000 * 60 < Date.now());
                }),
                tap(([a, b, c]) => {
                    // const logKey = 'Test LoadTables';
                    // this.LOGGER.track(logKey);
                    Promise
                        .all([
                            this.Entity.get('employee').get({id: c.person.id, select: ['name','languages','language', 'loginEnabled'], join: ['orderConsumers'], sort: { 'orderConsumers.position' : 'asc' } }),
                            this.Entity.get<OrderConsumer>('order_consumer').search({ sort: { position: 'asc' }, partialJoin: { 'consumerBookings': ['id'] } })
                        ])
                        .then((values) => {
                            if (values[0].orderConsumers && values[0].orderConsumers.length) {
                                return values[0].orderConsumers;
                            } else {
                                return values[1];
                            }
                        })
                        .then((payload: OrderConsumer[]) => {
                            // this.LOGGER.trackComplete(logKey);
                            this.store.dispatch(setTables({ payload }));
                            this.lastTableLoadTimeStamp = Date.now();
                        });

                })
            ),
        { dispatch: false }
    );

    setTablesMap$ = createEffect(() => this.actions$
            .pipe(
                ofType(PosActions.SetTables),
                tap((A: { payload: OrderConsumer[]; type: string }) => {
                    let payload: { [key: number]: OrderConsumer } = {};
                    A.payload.forEach((table: OrderConsumer) => payload[table.id] = table);
                    this.store.dispatch(setTableMap({ payload }));
                })
            ),
        { dispatch: false }
    );

  updateTables$ = createEffect(() => this.actions$
      .pipe(
        ofType(PosActions.UpdateTables),
        switchMap(() => this.store.select(getPosTables).pipe(take(1))),
        tap((tables) => {
          const select = ['id', 'name', 'status', 'color', 'guestNumberEnter', 'position'];
          const partialJoin = { 'consumerBookings': ['id'] }
          if (this.LocalStorage.getItem(OPTIONS_TABLE_LAYOUT)) select.push('metadata');
          if (tables?.length) {
            const activeTableId = safeMapId(tables);
            this.Entity.get<OrderConsumer>('order_consumer')
              .search({ select, partialJoin, sort: { position: 'asc' } })
              .then((response) => {
                // sometimes requests fail
                if (response) {
                  const tableDataIdMap = response
                      .filter((table) => activeTableId.includes(table.id))
                      .reduce((accumulator: any, item: any) => {
                    accumulator[item.id] = item;

                    return accumulator;
                  }, {});

                  if (Object.keys(tableDataIdMap).length !== tables?.length) tables = Object.values(tableDataIdMap);
                  this.store.dispatch(setTables({
                    payload: tables
                      .map((table) => ({ ...table, ...tableDataIdMap[table.id] }))
                      // .sort((a: OrderConsumer, b: OrderConsumer) => a.position - b.position)
                  }));
                }

              })
              .catch((e) => {
                console.warn(e);
              });
          }


        })
      ),
    { dispatch: false }
  );

    updateTable$ = createEffect(() => this.actions$
            .pipe(
                ofType(updateTable),
                switchMap((action: { payload: number }) =>
                    this.store.select(getPosTables).pipe(
                        take(1),
                        map((tables) => [action.payload, tables])
                    )
                ),
                tap(([id, tables]: [number, OrderConsumer[]]) => {
                    if (tables?.length) {
                        this.Entity.get<OrderConsumer>('order_consumer')
                            .get({ select: ['id', 'status'], id })
                            .then((response) => {
                                // sometimes requests fail
                                if (response) {

                                    this.store.dispatch(setTables({
                                        payload:
                                            tables.map((table) => {
                                                if (table.id === response.id) {
                                                    return { ...table, response };
                                                }
                                                return table;
                                                }
                                            )
                                    }));
                                }

                            })
                            .catch((e) => {
                                console.warn(e);
                            });
                    }


                })
            ),
        { dispatch: false }
    );

    closeTable$ = createEffect(() => this.actions$
            .pipe(
                ofType(closeTable),
                switchMap((action: { payload: number }) =>
                    this.store.select(getPosTables).pipe(
                        take(1),
                        map((tables) => [action.payload, tables])
                    )
                ),
                tap(([id, tables]: [number, OrderConsumer[]]) => {
                    if (tables?.length) {
                        this.store.dispatch(setTables({
                            payload:
                                tables.map((table) => {
                                    if (table.id === id) {
                                        return { ...table, orders: [], status: 'open' };
                                    }
                                    return table;
                                    }
                                )
                        }));

                    }


                })
            ),
        { dispatch: false }
    );

    loadAreas$ = createEffect(() => this.actions$
            .pipe(
                ofType(PosActions.LoadAreas),
                withLatestFrom(this.store.select(selectPosState), ),
                filter(([action, state]) => {
                    return !isDefined(state.areas);
                }),
                tap(([a, b]) => {
                    this.Entity.get('area').search()
                        .then((payload: Area[]) => {
                            this.store.dispatch(setAreas({ payload }));
                        });

                })
            ),
        { dispatch: false }
    );

    loadMenu$ = createEffect(() => this.actions$
            .pipe(
                ofType(PosActions.LoadMenu),
                withLatestFrom(this.store.select(selectPosState)),
                filter(([action, state]) => {
                    return !isDefined(state.menu);
                }),
                tap(() => {
                    this.store.dispatch(setMenu({ payload: [] }));

                    const articleEntity = this.Entity.get('article');
                    let promise: Promise<Article[]>;

                    if (this.LocalStorage.getItem(OPTIONS_ITEMS_IN_WAREHOUSE)) {
                        promise = articleEntity.getPosList({ stored: 1 });
                    } else {
                        // let request: any = {
                        //     confirmed: true,
                        //     join: ['categories', 'productParameters', 'productParameters.parameter', 'productParameters.parameter.dropDownOptions', 'photo']
                        // };
                        // if (this.LocalStorage.getItem(OPTIONS_TIME_RESTRICTION) === 'true') request.timeTag = '';
                        // promise = articleEntity.search(request);
                        promise = articleEntity.getPosList({ stored: 0 });

                    }

                    promise.then((payload) => {
                        payload.sort((a, b) => +a.position < +b.position ? -1 : 1);
                        payload.forEach((item) => {

                            if (!item.price && item.primeCostExpression && !isNaN(parseFloat(item.primeCostExpression))) {
                                item.price = parseFloat(item.primeCostExpression);
                            }
                            let price = !isNaN(parseFloat(item.primeCostExpression)) ? parseFloat(item.primeCostExpression) : (item.price ?? 0.0);
                            price = roundNumber((item.vat ? price * (1 + item.vat?.percentage / 100) : price), 2);
                            item.price = `${ numberDecimalComma(price.toString()) } ${ this.Currencies?.base?.shorthand }` as unknown as number;
                        });

                        this.store.dispatch(setMenu({ payload }));
                        this.store.dispatch(loadParameters());

                        const request: { [key: string]: unknown } = { join: ['parent'], active: true, sort: { position: 'asc' } };

                        if (this.LocalStorage.getItem(OPTIONS_TIME_RESTRICTION)) request.timeTag = '';

                        this.Entity.get({ name: 'article_category' })
                            .search(request)
                            .then((payload) => {
                                this.store.dispatch(setCategories({ payload }));
                            });

                    })
                        .catch((e) => {
                            this.Toast.pop('error', this.Filter.translate('error_loading_menu_items'));
                        });

                })
            ),
        { dispatch: false }
    );

    loadParameters$ = createEffect(() => this.actions$
            .pipe(
                ofType(PosActions.LoadParameters),
                withLatestFrom(this.store.select(selectPosState)),
                filter(([action, state]) => {
                    return !isDefined(state.parameters);
                }),
                tap(() => {
                    this.Entity.get('parameter')
                        .search({ join: ['dropDownOptions'], translate: true })
                        .then((response) => {
                            if (!response) return;
                            let options: ParameterDropdownOption[] = [];

                            const dropdownParameters = response.filter((_parameter) => _parameter.type === 'dropdown');

                            dropdownParameters.forEach((param) => options = options.concat(param.dropDownOptions.map((option)  => ({ ...option, parameter: param }) )));

                            this.store.dispatch(setDropdownOptions({ payload: options }));
                            this.store.dispatch(setDropdownParameters({ payload: response }));
                        });
                })
            ),
        { dispatch: false }
    );

    loadMaterials$ = createEffect(() => this.actions$
            .pipe(
                ofType(PosActions.LoadMaterials),
                withLatestFrom(this.store.select(selectPosState)),
                filter(([action, state]) => {
                    return !isDefined(state.materials);
                }),
                tap(() => {
                    this.Entity.get('material').search()
                        .then((response) => this.store.dispatch(setMaterials({ payload: response })));
                })
            ),
        { dispatch: false }
    );

    loadFeatured$ = createEffect(() => this.actions$
            .pipe(
                ofType(PosActions.LoadFeatured),
                withLatestFrom(this.store.select(selectPosState)),
                filter(([action, state]) => {
                    return !isDefined(state.featured);
                }),
                tap(() => {
                    this.Entity
                        .get('article')
                        .search({
                            featured: true,
                            join: ['productParameters', 'productParameters.parameter',  'productParameters.parameter.dropDownOptions']
                        })
                        .then((payload) => {

                            this.store.dispatch(setFeatured({ payload }));
                        });
                })
            ),
        { dispatch: false }
    );

    loadChildArticles$ = createEffect(() => this.actions$
            .pipe(
                ofType(PosActions.LoadChildArticles),
                withLatestFrom(this.store.select(selectPosState)),
                filter(([action, state]) => {
                    return !isDefined(state.childArticles);
                }),
                tap(() => {
                    this.Entity.get('article')
                        .search({
                            confirmed: true,
                            join: [
                                'articleChildes',
                                'articleChildes.child',
                                'articleChildes.child.photo',
                                'articleChildes.child.categories',
                                'articleChildes.child.productParameters',
                                'articleChildes.child.productParameters.parameter',
                                'articleChildes.child.productParameters.parameter.dropDownOptions',
                            ],
                            'articleChildes.optional': true,
                            sort: { 'articleChildes.position': 'asc' },
                        })
                        .then((response) => {
                            const result = response?.length ? response.map((item) => {
                                item.children = item.articleChildes.map((child) => ({ ...child.child, quantity: child.quantity, position: child.position }));
                                item.children.sort((a, b) =>  a.position < b.position ? -1 : 1);
                                return item;
                            }) : [];

                            this.store.dispatch(setChildArticles({ payload: result }));
                        });
                })
            ),
        { dispatch: false }
    );

  loadDevices$ = createEffect(() => this.actions$
      .pipe(
        ofType(SystemOptionsActions.SetData),
        filter(value => !!value),
        tap((action: { payload: SystemOptions } ) => {
          const systemOptions = action.payload;
          if (systemOptions) {
            const type = (systemOptions.useFiscal ? 'FiscalPrinter' : 'PosPrinter');
            this.Entity.get('device').search({ type }).then((response: Device[]) => {
              if (response.length === 1) this.LocalStorage.setItem(OPTIONS_PRINTER_ID, response[0].id);
              response.forEach((item: Device) => item.name = item.configuration?.name ?? (systemOptions.useFiscal ? 'FiscalPrinter' : 'PosPrinter'));

              this.store.dispatch(setDevices({ payload: response }));

            });
          }

        })
      ),
    { dispatch: false }
  );

  loadPosOptionsOverrides$ = createEffect(() => this.actions$.pipe(
      ofType(PosActions.LoadPosOptionsOverrides),
      tap(() => {
        this.Request.get(`${CONFIG.api}public/pos/global_overrides`).then((options) => {
          const payload: any = {};
          const filteredOptions = Object.entries(options)
            .filter(([key, value]) => `${value}` !== 'null' && value !== null && `${value}` !== '-');

          for (let [key, value] of filteredOptions) {
            switch (key) {
              case 'autoLogoutTimer':
                this.LocalStorage.setItem('options_' + key, parseInt(`${value}`));
                break
              case 'restrictConfirmedOrderChanges':
              case 'ignoreOrderRemoveReason':
                value = JSON.parse(value as any);
                break;
              default:
                this.LocalStorage.setItem('options_' + key, value);
                break;
            }

            payload[key] = value as string;
          }

          this.store.dispatch(setPosOptionsOverrides({ payload }));
        })
      })
    ),
    { dispatch: false }
  );

  clearTablesOnLogout$ = createEffect(() => this.actions$.pipe(
      ofType(userLogout, setCurrUser),
      tap(() => this.store.dispatch(clearTables()))
  ), { dispatch: false });

    constructor(
        private actions$: Actions,
        private store: Store,
        private Entity: Entity,
        private LocalStorage: LocalStorageService,
        private Currencies: CurrenciesService,
        private Toast: ToastService,
        private Filter: FilterService,
        private Request: RequestService,
    ) {
        this.lastTableLoadTimeStamp = Date.now();
    }

}
